import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import type { VendorGuidelineQuestion } from "../types/marketplace.types";

/** Visual density — `compact` in the connect wizard, `card` in the adjust drawer. */
type SelectAnswerVariant = "compact" | "card";

interface SelectAnswerPickerProps {
  question: VendorGuidelineQuestion;
  /** JSON-encoded `string[]` of selected options (the stored answer format). */
  value: string;
  onChange: (value: string) => void;
  variant?: SelectAnswerVariant;
}

const FIELDSET_CLASS: Record<SelectAnswerVariant, string> = {
  compact: "flex flex-col gap-2.5",
  card: "flex flex-col gap-3 rounded-lg border border-border bg-card p-4",
};

const OPTION_BASE_CLASS: Record<SelectAnswerVariant, string> = {
  compact:
    "inline-flex min-h-9 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-[border-color,background-color,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 motion-reduce:transition-none",
  card: "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm transition-[border-color,background-color,color,box-shadow] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 motion-reduce:transition-none",
};

const OPTION_CHECKED_CLASS: Record<SelectAnswerVariant, string> = {
  compact: "border-primary bg-primary/5 font-medium text-foreground",
  card: "border-primary bg-primary/10 font-medium text-foreground shadow-sm",
};

const OPTION_UNCHECKED_CLASS: Record<SelectAnswerVariant, string> = {
  compact:
    "border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/40 hover:text-foreground",
  card: "border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-muted/40 hover:text-foreground",
};

/**
 * Renders a SELECT guideline question as a grid of toggle pills. Shared by the
 * connect wizard and the adjust-guidelines drawer so single/multiple-choice
 * behaviour and the stored JSON answer shape stay identical in both places.
 */
export function SelectAnswerPicker({
  question,
  value,
  onChange,
  variant = "compact",
}: SelectAnswerPickerProps) {
  const { t } = useTranslation();
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = parseSelected(value);
  const allowMultiple = question.allowMultiple ?? true;
  const options = question.options;

  function handleSelect(option: string) {
    if (allowMultiple) {
      const next = selected.includes(option)
        ? selected.filter((s) => s !== option)
        : [...selected, option];
      onChange(JSON.stringify(next));
      return;
    }
    // Single choice: clicking a selected option clears it, otherwise replaces.
    onChange(JSON.stringify(selected.includes(option) ? [] : [option]));
  }

  function focusOption(index: number) {
    optionRefs.current[index]?.focus();
  }

  function handleOptionKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    option: string,
    index: number,
  ) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleSelect(option);
      return;
    }

    if (allowMultiple) return;

    const count = options.length;
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      nextIndex = (index + 1) % count;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      nextIndex = (index - 1 + count) % count;
    }

    if (nextIndex !== null) {
      handleSelect(options[nextIndex]!);
      focusOption(nextIndex);
    }
  }

  return (
    <fieldset
      className={FIELDSET_CLASS[variant]}
      role={allowMultiple ? "group" : "radiogroup"}
    >
      <legend className="text-sm font-medium text-foreground">
        {question.prompt}
        {!question.isRequired && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            ({t("marketplace.wizard.questions.optional")})
          </span>
        )}
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option, index) => {
          const checked = selected.includes(option);
          const isRadioTabStop =
            !allowMultiple && (checked || (selected.length === 0 && index === 0));
          return (
            <button
              key={option}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              type="button"
              role={allowMultiple ? "checkbox" : "radio"}
              aria-checked={checked}
              tabIndex={allowMultiple ? 0 : isRadioTabStop ? 0 : -1}
              onClick={() => handleSelect(option)}
              onKeyDown={(event) => handleOptionKeyDown(event, option, index)}
              className={cn(
                OPTION_BASE_CLASS[variant],
                checked ? OPTION_CHECKED_CLASS[variant] : OPTION_UNCHECKED_CLASS[variant],
              )}
            >
              {checked && (
                <Icon
                  name="check"
                  size="xs"
                  color={variant === "card" ? "primary" : "inherit"}
                  className="-ml-0.5 animate-in zoom-in-50 duration-150 motion-reduce:animate-none"
                />
              )}
              {option}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function parseSelected(value: string): string[] {
  if (!value) return [];
  try {
    return JSON.parse(value) as string[];
  } catch {
    return [];
  }
}
